import { Link } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { collections } from '../../../mocks/products';

export default function FeaturedCollections() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const o = new IntersectionObserver(([e]) => { if (e.isIntersecting) ref.current?.classList.add('visible'); }, { threshold: 0.1 });
    if (ref.current) o.observe(ref.current);
    return () => o.disconnect();
  }, []);

  const [lr, bed, dining, office, outdoor] = collections;

  return (
    <section className="py-28 bg-cream-DEFAULT">
      <div ref={ref} className="fade-up max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16 gap-4">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="w-8 h-px bg-gold" />
              <span className="text-gold text-xs font-semibold uppercase tracking-[0.25em]">Our Collections</span>
            </div>
            <h2 className="font-serif text-5xl md:text-6xl font-bold text-dark leading-tight">
              Curated for<br />Every Room
            </h2>
          </div>
          <Link
            to="/collections"
            className="inline-flex items-center gap-3 border border-dark/20 text-dark px-6 py-3 rounded text-xs font-semibold uppercase tracking-wider hover:bg-dark hover:text-cream-DEFAULT transition-all cursor-pointer whitespace-nowrap self-start md:self-auto"
          >
            All Collections
            <i className="ri-arrow-right-line" />
          </Link>
        </div>

        {/* Asymmetric Grid */}
        <div className="grid grid-cols-12 gap-4 h-auto">
          {/* Living Room - large left */}
          <div className="col-span-12 md:col-span-7 group relative overflow-hidden rounded-2xl h-[600px] cursor-pointer">
            <img
              src={lr.image}
              alt="Living room furniture near me Las Vegas"
              title="Living Room Collection"
              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-dark/70 via-dark/20 to-transparent" />
            <div className="absolute bottom-8 left-8 right-8">
              <span className="text-gold text-xs font-semibold uppercase tracking-[0.2em] mb-2 block">{lr.productCount}+ Pieces</span>
              <h3 className="font-serif text-3xl font-bold text-white mb-3">{lr.name}</h3>
              <p className="text-white/70 text-sm mb-5 leading-relaxed max-w-xs">{lr.tagline}</p>
              <Link
                to={lr.slug}
                className="inline-flex items-center gap-2 text-white text-sm font-semibold hover:text-gold transition-colors cursor-pointer group/link"
              >
                Shop Now
                <i className="ri-arrow-right-line group-hover/link:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>

          {/* Right column - stacked */}
          <div className="col-span-12 md:col-span-5 flex flex-col gap-4">
            {/* Bedroom */}
            <div className="group relative overflow-hidden rounded-2xl h-[290px] cursor-pointer">
              <img
                src={bed.image}
                alt="Bedroom furniture modern collection"
                title="Bedroom Collection"
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-dark/60 via-dark/30 to-transparent" />
              <div className="absolute bottom-6 left-6">
                <span className="text-gold text-xs font-semibold uppercase tracking-[0.2em] mb-1 block">{bed.productCount}+ Pieces</span>
                <h3 className="font-serif text-2xl font-bold text-white mb-3">{bed.name}</h3>
                <Link to={bed.slug} className="inline-flex items-center gap-2 text-white text-sm font-semibold hover:text-gold transition-colors cursor-pointer">
                  Shop Now <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>

            {/* Dining */}
            <div className="group relative overflow-hidden rounded-2xl h-[290px] cursor-pointer">
              <img
                src={dining.image}
                alt="Dining room furniture elegant collection"
                title="Dining Room Collection"
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-dark/60 via-dark/30 to-transparent" />
              <div className="absolute bottom-6 left-6">
                <span className="text-gold text-xs font-semibold uppercase tracking-[0.2em] mb-1 block">{dining.productCount}+ Pieces</span>
                <h3 className="font-serif text-2xl font-bold text-white mb-3">{dining.name}</h3>
                <Link to={dining.slug} className="inline-flex items-center gap-2 text-white text-sm font-semibold hover:text-gold transition-colors cursor-pointer">
                  Shop Now <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          {[office, outdoor].map((col) => (
            <div key={col.id} className="group relative overflow-hidden rounded-2xl h-[300px] cursor-pointer">
              <img
                src={col.image}
                alt={`${col.name} furniture collection`}
                title={`${col.name} Collection`}
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-dark/65 via-dark/20 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
                <div>
                  <span className="text-gold text-xs font-semibold uppercase tracking-[0.2em] mb-1 block">{col.productCount}+ Pieces</span>
                  <h3 className="font-serif text-2xl font-bold text-white">{col.name}</h3>
                </div>
                <Link
                  to={col.slug}
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-white/20 hover:bg-gold text-white transition-colors cursor-pointer flex-shrink-0"
                >
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
